'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { LoadingMorph } from '@extern/ui/loading-morph';
import dynamic from 'next/dynamic';
import { usePathname, useSearchParams } from 'next/navigation';
import { useAuthEvents } from '@/app/(auth)/_hooks/use-auth-events';
import { parseLabsArrival } from '@/app/(auth)/sign-up/_utils/labs-arrival';
import { parsePortfolioArrival } from '@/app/(auth)/sign-up/_utils/portfolio-arrival';
import { useSignUpQ1Variant } from '../_hooks/use-sign-up-q1-variant';
import { AuthLayout } from './auth-layout';
import { CenteredAuthLayout } from './centered-auth-layout';

/**
 * Loaded only when the URL says the visitor just claimed a portfolio, so the panel and its lookup
 * never reach the bundle of an ordinary sign-up or sign-in load.
 */
const PortfolioArrivalLayout = dynamic(() => import('./portfolio-arrival/portfolio-arrival-layout').then(mod => mod.PortfolioArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});

/**
 * The Labs arrival layout is loaded only when a URL actually carries a lab result, so the packs it
 * renders from never reach the bundle of an ordinary sign-up or sign-in load.
 */
const LabsArrivalLayout = dynamic(() => import('./labs-arrival/labs-arrival-layout').then(mod => mod.LabsArrivalLayout), {
  loading: () => <AuthLoading />,
  ssr: false
});
function AuthLoading() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex min-h-screen w-full flex-col items-center justify-center bg-role-surface"><LoadingMorph /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
const CONTROL_LAYOUT_TEXT = <>
    Gain Real Experience.
    <br />
    Build Essential Skills.
    <br />
    Drive Your Career Forward.
  </>;

/**
 * What `auth_screen_viewed` carries for the two arrivals that get their own layout. The two are told
 * apart by campaign tags that never co-occur; the portfolio one is checked first to match the render.
 */
function arrivalEventProps(portfolioArrival: ReturnType<typeof parsePortfolioArrival>, labsArrival: ReturnType<typeof parseLabsArrival>): Record<string, unknown> | undefined {
  if (portfolioArrival) {
    return {
      portfolio_arm: portfolioArrival.arm,
      portfolio_handoff: true
    };
  }
  if (labsArrival) {
    return {
      labs_archetype: labsArrival.archetypeId,
      labs_rail: true
    };
  }
  return;
}
interface AuthLayoutSwitcherProps {
  children: ReactNode;
}
export function AuthLayoutSwitcher(t0) {
  const $ = _c(25);
  const {
    children
  } = t0;
  const {
    isReady,
    variant
  } = useSignUpQ1Variant();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    trackAuthScreenViewed
  } = useAuthEvents();
  const lastTrackedPathRef = useRef(null);
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t1 = pathname?.includes("/sign-up") ? parsePortfolioArrival(searchParams) : null;
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const portfolioArrival = t1;
  let t2;
  if ($[3] !== pathname || $[4] !== searchParams) {
    t2 = pathname?.includes("/sign-up") ? parseLabsArrival(searchParams) : null;
    $[3] = pathname;
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const labsArrival = t2;
  let t3;
  let t4;
  if ($[6] !== isReady || $[7] !== labsArrival || $[8] !== pathname || $[9] !== portfolioArrival || $[10] !== trackAuthScreenViewed) {
    t3 = () => {
      if (!(isReady && pathname)) {
        return;
      }
      if (lastTrackedPathRef.current === pathname) {
        return;
      }
      if (pathname.includes("/sign-up")) {
        trackAuthScreenViewed("sign_up", "entry", "start", arrivalEventProps(portfolioArrival, labsArrival));
        lastTrackedPathRef.current = pathname;
        return;
      }
      if (pathname.includes("/sign-in")) {
        trackAuthScreenViewed("sign_in", "entry", "start");
        lastTrackedPathRef.current = pathname;
      }
    };
    t4 = [isReady, labsArrival, pathname, portfolioArrival, trackAuthScreenViewed];
    $[6] = isReady;
    $[7] = labsArrival;
    $[8] = pathname;
    $[9] = portfolioArrival;
    $[10] = trackAuthScreenViewed;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  if (!isReady) {
    let t5;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <AuthLoading />;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  if (portfolioArrival) {
    let t5;
    if ($[14] !== children || $[15] !== portfolioArrival || $[16] !== variant) {
      t5 = <PortfolioArrivalLayout arrival={portfolioArrival} variant={variant}>{children}</PortfolioArrivalLayout>;
      $[14] = children;
      $[15] = portfolioArrival;
      $[16] = variant;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  let t5;
  if ($[18] !== children || $[19] !== variant) {
    t5 = variant === "experiment" ? <CenteredAuthLayout>{children}</CenteredAuthLayout> : <AuthLayout text={CONTROL_LAYOUT_TEXT}>{children}</AuthLayout>;
    $[18] = children;
    $[19] = variant;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const shipped = t5;
  if (labsArrival) {
    let t6;
    if ($[21] !== children || $[22] !== labsArrival || $[23] !== shipped) {
      t6 = <LabsArrivalLayout fallback={shipped} params={labsArrival}>{children}</LabsArrivalLayout>;
      $[21] = children;
      $[22] = labsArrival;
      $[23] = shipped;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    return t6;
  }
  return shipped;
}